import { ChevronLeft, ChevronRight, MoreVertical, UserPlus } from "lucide-react";
import { useEffect, useState, useCallback, useRef } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Search,
  Plus,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Building2,
  User,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  FileText,
  Briefcase,
  DollarSign,
  Download,
} from "lucide-react";
import {
  getEmployees,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  getEmployeeById,
  uploadEmployeeExcel,
  downloadEmployeeTemplate,
  permanentDeleteEmployee,
  reactivateEmployee,
  downloadJoiningLetter,
  downloadOfferLetter,
  sendOfferLetterEmail,
  sendjoiningLetterEmail,
  downloadSalarySlip,
  sendSalarySlipEmail,
  uploadEmployeeDocument,
  getEmployeeDocuments,
  deleteEmployeeDocument,
  type EmployeeDocType,
} from "../../api/employees";
import { getCompanyComplianceSettings } from "../../api/officeandcompany";
import { getManagerList, getDepartmentList, getDesignationList } from "../../redux/slices/employee";
import { User as UserType } from "../../types";
import { useToast } from "../../hooks/useToast";
import { useFileDropPaste } from "../../hooks/useFileDropPaste";
import { useAuth } from "../../contexts/AuthContext";
import { country } from "../../_mock/country";
import { state } from "../../_mock/state";
import { dispatch, useSelector } from "../../redux/store";
import api from "../../api/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from "../../components/ui/dropdown-menu";
import termsConditionsApi from '../../api/TermsconditionApi';

// Sub-components extracted from this file (no logic changes — just file
// structure). See pages/Employee/.
import EmployeeForm, { EmployeeStepsTabNav, type EmployeeStep } from "./EmployeeForm";
import EmployeeDetails from "./EmployeeDetails";

// ==================== MAIN EMPLOYEES COMPONENT ====================

const EMPLOYEES_PAGE_SIZE = 9;

export function Employees() {
  const [employees, setEmployees] = useState<UserType[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  // In-dropdown search terms for the Department / Role filters above the grid.
  const [deptFilterSearch, setDeptFilterSearch] = useState("");
  const [roleFilterSearch, setRoleFilterSearch] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState<UserType | null>(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedEmployeeForDelete, setSelectedEmployeeForDelete] = useState<UserType | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const { dropRef: importDropRef, isDragOver: importDragOver } = useFileDropPaste({
    onFiles: (files) => {
      // Bulk import takes a single Excel — keep the first usable file.
      const picked = files.find((f) =>
        /\.(xlsx|xls)$/i.test(f.name) || f.type.includes('sheet') || f.type.includes('excel'),
      ) || files[0];
      if (picked) setFile(picked);
    },
    disabled: !showImportModal,
    pasteOnDocument: true,
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editInitialTab, setEditInitialTab] = useState<EmployeeStep>("personal");
  const [addActiveTab, setAddActiveTab] = useState<EmployeeStep>("personal");
  const [editActiveTab, setEditActiveTab] = useState<EmployeeStep>("personal");
  const [addUnlockedTabs, setAddUnlockedTabs] = useState<EmployeeStep[]>(["personal"]);
  const addBodyRef = useRef<HTMLDivElement>(null);
  const editBodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (showAddModal) {
      setAddActiveTab("personal");
      setAddUnlockedTabs(["personal"]);
    }
  }, [showAddModal]);
  useEffect(() => {
    if (showEditModal) setEditActiveTab(editInitialTab);
  }, [showEditModal, editInitialTab]);
  // Tab-change handler used by the externally-rendered tab bar. Mirrors
  // the in-form Back/Next behavior: change tab + reset body scroll.
  const makeTabChangeHandler =
    (setter: (t: EmployeeStep) => void, bodyRef: React.RefObject<HTMLDivElement>) =>
    (next: EmployeeStep) => {
      setter(next);
      if (bodyRef.current) bodyRef.current.scrollTop = 0;
    };
  const [showViewModal, setShowViewModal] = useState(false);
  const [activeTab, setActiveTab] = useState<"personal" | "employment" | "documents">("personal");
  const [selectedEmployeeForDocs, setSelectedEmployeeForDocs] = useState<UserType | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const [showMandatoryModal, setShowMandatoryModal] = useState(false);

  const [settings, setSettings] = useState({
    disable_mandatory: false,
    is_active: true,
  });

  // Supervisor is view-only and intentionally excluded from employee CRUD.
  const canManageEmployees = user?.role === "admin" || user?.role === "hr head" || user?.role === "manager" || user?.role === "director" || user?.role === "founder";

  const departmentList = useSelector((state: any) => state.employee.departmentList) || [];

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    dispatch(getDesignationList());
    dispatch(getDepartmentList());
  }, []);

  useEffect(() => {
    filterEmployees();
  }, [employees, searchQuery, departmentFilter, roleFilter, departmentList]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, departmentFilter, roleFilter]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredEmployees.length / EMPLOYEES_PAGE_SIZE));
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [filteredEmployees, currentPage]);

  useEffect(() => {
    fetchMandatorySettings();
  }, []);

  const fetchMandatorySettings = async () => {
    try {
      const res = await api.get("/get_employee_setting");
      // setSettings(res.data);
      setSettings({
        disable_mandatory: res.data?.employee_setting?.disable_mandatory ?? false,
        is_active: res.data?.employee_setting?.is_active ?? true,
      });
    } catch (err) {
      console.error("Error fetching settings", err);
    }
  };

  const fetchEmployees = async () => {
    try {
      console.log("Fetching employees...");
      const response = await getEmployees();
      console.log("Employees fetched successfully:", response.employees?.length || 0);
      setEmployees(response.employees || []);
    } catch (error: any) {
      console.error("Error fetching employees:", error);
      toast({
        // title: "Error",
        description: error.message || error.detail || "Failed to load employees",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const designationList = useSelector((state: any) => state.employee.designationList);

  const getDesignationName = (designationId: string) => {
    const designation = designationList.find((d: any) => d.id === designationId);
    return designation?.name || designationId;
  };

  // Prefer the live name resolved from the master list (using the employee's
  // stored designation_id), so renaming a designation in settings reflects
  // immediately. Falls back to the stored denormalised string when the id is
  // missing or doesn't match the master.
  const resolveDesignation = (emp: any) => {
    const byId = emp?.designation_id
      ? (designationList as any[]).find((d: any) => d?.id === emp.designation_id)?.name
      : '';
    return byId || emp?.designation || '—';
  };

  const filterEmployees = () => {
    const search = searchQuery.trim().toLowerCase();
    const wantDept = departmentFilter !== 'all';
    const wantRole = roleFilter !== 'all';
    const selectedDeptId = String(departmentFilter).toLowerCase().trim();

    const filtered = (employees || []).filter((emp: any) => {
      if (search) {
        const hit =
          emp.first_name?.toLowerCase().includes(search) ||
          emp.email?.toLowerCase().includes(search) ||
          emp.employeeID?.toLowerCase().includes(search);
        if (!hit) return false;
      }
      if (wantDept) {
        const empDeptId = String(emp.department_id ?? '').toLowerCase().trim();
        if (empDeptId !== selectedDeptId) return false;
      }
      if (wantRole && emp.role !== roleFilter) return false;
      return true;
    });

    setFilteredEmployees(filtered);
  };

  const handleAddEmployee = async (employeeData: Partial<UserType>) => {
    try {
      console.log("Adding new employee:", employeeData);
      const created = await createEmployee(employeeData);
      toast({
        title: "Success",
        description: "Employee added successfully",
      });
      setShowAddModal(false);
      fetchEmployees();
      // Return the created employee so EmployeeForm can upload any staged documents.
      return created?.employee;
    } catch (error: any) {
      console.error("Error adding employee:", error);
      const description =
        error?.message ||
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        'Failed to add employee';
      toast({
        title: 'Failed to add employee',
        description,
        variant: 'destructive',
      });
      throw error;
    }
  };

  const handleEditEmployee = async (employeeData: Partial<UserType>) => {
    console.log("Updating employee:", employeeData.id);
    console.log("Payload:", employeeData);

    if (!employeeData.id) {
      toast({
        // title: "Error",
        description: "Employee ID missing",
        variant: "destructive",
      });
      return;
    }

    await updateEmployee(employeeData.id, employeeData);
    const fresh = await getEmployeeById(employeeData.id);
    setSelectedEmployee(fresh.employee);

    toast({
      title: "Success",
      description: "Employee updated successfully",
    });

    setShowEditModal(false);
    fetchEmployees();
  };

  const handleDeleteClick = (employee: UserType) => {
    setSelectedEmployeeForDelete(employee);
    setDeleteModalOpen(true);
  };

  const handleSoftDelete = async () => {
    if (!selectedEmployeeForDelete) return;
    await deleteEmployee(selectedEmployeeForDelete.id);
    toast({ title: "Employee deactivated" });
    setDeleteModalOpen(false);
    fetchEmployees();
  };

  const handlePermanentDelete = async () => {
    if (!selectedEmployeeForDelete) return;
    await permanentDeleteEmployee(selectedEmployeeForDelete.id);
    toast({ title: "Employee permanently deleted" });
    setDeleteModalOpen(false);
    fetchEmployees();
  };

  const handleReactivate = async () => {
    if (!selectedEmployeeForDelete) return;
    await reactivateEmployee(selectedEmployeeForDelete.id);
    toast({ title: "Employee reactivated" });
    setDeleteModalOpen(false);
    fetchEmployees();
  };

  // const handleViewEmployee = async (id: string) => {
  //   try {
  //     console.log("Fetching employee details:", id);
  //     const response = await getEmployeeById(id);
  //     setSelectedEmployee(response.employee);
  //     setShowViewModal(true);
  //   } catch (error: any) {
  //     console.error("Error fetching employee details:", error);
  //     toast({
  //       title: "Error",
  //       description: error.message || "Failed to load employee details",
  //       variant: "destructive",
  //     });
  //   }
  // };

  const handleViewEmployee = async (id: string, openTab?: "personal" | "employment" | "documents") => {
    try {
      console.log("Fetching employee details:", id);
      const response = await getEmployeeById(id);
      setSelectedEmployee(response.employee);
      setActiveTab(openTab || "personal");
      setShowViewModal(true);
    } catch (error: any) {
      console.error("Error fetching employee details:", error);
      toast({
        // title: "Error",
        description: error.message || error.detail || "Failed to load employee details",
        variant: "destructive",
      });
    }
  };

  // Add function to open documents tab directly
  const handleOpenDocumentsTab = (employee: UserType) => {
    handleViewEmployee(employee.id, "documents");
  };


  const handleEditClick = async (id: string) => {
    try {
      const data = await getEmployeeById(id);
      setSelectedEmployee(data.employee);
      setShowEditModal(true);
    } catch (error: any) {
      toast({
        // title: "Error",
        description: error.message || error.detail || "Failed to load employee",
        variant: "destructive",
      });
    }
  };

  
  // Picks a palette index 0-5 from the first character of the name. The
  // actual colors are defined by .gt-emp-card__top-bar / __avatar in
  // global-theme.css keyed by data-palette="0".."5".
  const paletteIndex = (name?: string) =>
    ((name?.charCodeAt(0) || 0) % 6).toString();

  // Canonical role whitelist — always shown, regardless of whether any
  // employee currently holds that role. Keeps junk values like "string"
  // out of the Roles filter dropdown.
  const roles = [
    "employee",
    "manager",
    "supervisor",
    "hr head",
    "director",
    "founder",
  ];
  const [importResults, setImportResults] = useState<{
    success: Array<{ name: string; email: string; employeeId?: string }>;
    failed: Array<{ name: string; email: string; reason: string }>;
    show: boolean;
  }>({ success: [], failed: [], show: false });


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="gt-shell min-h-screen">
      <div className="max-w-7xl mx-auto p-4 md:p-7 space-y-6 gt-fade-in">
        <div className="gt-page-hero flex-col md:flex-row gap-4">
          <div>
            <div className="gt-eyebrow-row">
              <span className="gt-leaf" />
              <span className="gt-eyebrow">ii. People</span>
            </div>
            <h1 className="gt-page-title">Employee <em>Management</em></h1>
            <p className="gt-page-sub" style={{ marginTop: 6 }}>
              Manage your organization's <em className="gt-serif-italic">workforce</em>.
            </p>
          </div>
          <div className="flex flex-row flex-wrap md:flex-nowrap items-start gap-2 self-start">
            {canManageEmployees && (

              <>
                <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
                  <DialogTrigger asChild>
                    <Button className="gt-btn is-primary">
                      <Plus className="h-4 w-4" />
                      Add Employee
                    </Button>
                  </DialogTrigger>
                  {/* Two-region layout: a fixed top region (title +
                      description + the lifted 4-step tab bar) stays put
                      while only the body underneath scrolls. The body
                      ref lets us reset its scrollTop when the user jumps
                      tabs, matching the in-form Back/Next behavior. */}
                  <DialogContent className="max-w-6xl bg-white max-h-[90vh] overflow-hidden p-0 gap-0 flex flex-col">
                    <div className="shrink-0 bg-white">
                      <DialogHeader className="px-6 pt-6 pb-3">
                        <DialogTitle>Add New Employee</DialogTitle>
                        <DialogDescription>
                          Fill in the details to add a new employee to your organization.
                        </DialogDescription>
                      </DialogHeader>
                      <EmployeeStepsTabNav
                        activeTab={addActiveTab}
                        unlockedTabs={addUnlockedTabs}
                        onTabChange={makeTabChangeHandler(setAddActiveTab, addBodyRef)}
                        className="px-2"
                      />
                    </div>
                    <div ref={addBodyRef} className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
                      <EmployeeForm
                        onSubmit={handleAddEmployee}
                        currentUserRole={user?.role || "admin"}
                        activeTab={addActiveTab}
                        onActiveTabChange={setAddActiveTab}
                        onUnlockedTabsChange={setAddUnlockedTabs}
                        existingEmails={(employees || [])
                          .map((e: any) => e?.email)
                          .filter(Boolean)}
                        existingPhones={(employees || [])
                          .map((e: any) => e?.phone)
                          .filter(Boolean)}
                        hideTabNav
                      />
                    </div>
                  </DialogContent>
                </Dialog>

                <Button
                  onClick={() => setShowImportModal(true)}
                  className="gt-btn is-primary"
                >
                  <Plus className="h-4 w-4" />
                  Import Employee
                </Button>


                <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
                  <DialogContent className="max-w-md bg-white">
                    <DialogHeader>
                      <DialogTitle>Import Employees</DialogTitle>
                      <DialogDescription>
                        Upload Excel file to import employees in bulk
                      </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={async () => {
                          try {
                            await downloadEmployeeTemplate();
                          } catch (err: any) {
                            console.error("Template download error:", err);
                            toast({
                              description:
                                err?.response?.data?.detail ||
                                err?.message ||
                                "Could not download template.",
                              variant: "destructive",
                            });
                          }
                        }}
                      >
                        Download Sample Excel
                      </Button>

                      <div
                        ref={importDropRef}
                        tabIndex={-1}
                        className={`rounded-lg outline-none transition-all ${
                          importDragOver ? 'ring-2 ring-blue-400 ring-offset-2 bg-blue-50/40 p-3' : ''
                        }`}
                      >
                        <Label>Select Excel File</Label>
                        <Input
                          type="file"
                          accept=".xlsx,.xls"
                          onChange={(e) => setFile(e.target.files?.[0] || null)}
                        />
                        <p className="text-[11px] text-gray-500 mt-1">
                          Or drag &amp; drop the file here, or paste with Ctrl/Cmd+V.
                        </p>
                        {file && (
                          <p className="text-xs text-emerald-700 mt-1 truncate">
                            Selected: {file.name}
                          </p>
                        )}
                      </div>

                      <Button
                        className="w-full bg-blue-600 text-white"
                        onClick={async () => {
                          if (!file) {
                            toast({
                              // title: "Error",
                              description: "Please select a file",
                              variant: "destructive",
                            });
                            return;
                          }
                          try {
                            const response = await uploadEmployeeExcel(file);
                            console.log("Import response:", response);

                            // Extract results from response
                            // const successList = response.successful_imports || response.success || [];
                            // const failedList = response.failed_imports || response.failed || [];

                            const successList = response.created || [];
                            const failedList = response.failed || [];

                            setImportResults({
                              success: successList.map((item: any) => ({
                                name: item.name || item.full_name || item.employeeID || "-",
                                email: item.email,
                                employeeId: item.employeeID || item.employee_id
                              })),
                              failed: failedList.map((item: any) => ({
                                name: item.name || item.employeeID || "Unknown",
                                email: item.email || "N/A",
                                reason: item.reason || item.error || "Invalid data format"
                              })),
                              show: true
                            });

                            toast({
                              title: "Import Completed",
                              description: `${successList.length} employees imported successfully, ${failedList.length} failed`,
                              variant: successList.length > 0 ? "default" : "destructive",
                            });

                            setShowImportModal(false);
                            setFile(null);
                            fetchEmployees();
                          } catch (err: any) {
                            console.error("Import error:", err);

                            // Check if error contains detailed results
                            if (err.response?.data) {
                              const errorData = err.response.data;
                              const successList = errorData.successful_imports || errorData.success || [];
                              const failedList = errorData.failed_imports || errorData.failed || [];

                              if (successList.length > 0 || failedList.length > 0) {
                                setImportResults({
                                  success: successList.map((item: any) => ({
                                    name: item.name || item.full_name || `${item.first_name} ${item.last_name}`,
                                    email: item.email,
                                    employeeId: item.employeeID || item.employee_id
                                  })),
                                  failed: failedList.map((item: any) => ({
                                    name: item.name || item.row_data?.name || "Unknown",
                                    email: item.email || item.row_data?.email || "N/A",
                                    reason: item.reason || item.error || "Invalid data format"
                                  })),
                                  show: true
                                });
                                setShowImportModal(false);
                                setFile(null);
                                if (successList.length > 0) {
                                  fetchEmployees();
                                }
                                return;
                              }
                            }

                            toast({
                              title: "Import Failed",
                              description: err.message || err.response?.data?.message || "Upload failed. Please check file format.",
                              variant: "destructive",
                            });
                          }
                        }}
                      >
                        Upload Excel
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <Dialog open={importResults.show} onOpenChange={(open) => !open && setImportResults(prev => ({ ...prev, show: false }))}>
                  <DialogContent className="max-w-2xl bg-white max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                      <DialogTitle className="text-xl">Import Results</DialogTitle>
                      <DialogDescription>
                        Summary of employee import operation
                      </DialogDescription>
                    </DialogHeader>

                    <Tabs defaultValue="success" className="flex-1 overflow-hidden flex flex-col">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="success" className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-green-500"></div>
                          Successful ({importResults.success.length})
                        </TabsTrigger>
                        <TabsTrigger value="failed" className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-red-500"></div>
                          Failed ({importResults.failed.length})
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="success" className="flex-1 overflow-auto mt-4">
                        {importResults.success.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            <div className="text-4xl mb-2">📭</div>
                            <p>No employees were imported successfully</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="bg-green-50 p-3 rounded-lg mb-3">
                              <p className="text-green-800 text-sm font-medium">
                                ✅ {importResults.success.length} employee(s) added successfully
                              </p>
                            </div>
                            <div className="border rounded-lg overflow-hidden">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">EmpID</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                    {/* <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Employee ID</th> */}
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {importResults.success.map((employee, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                      <td className="px-4 py-2 text-sm text-gray-900">{employee.name}</td>
                                      <td className="px-4 py-2 text-sm text-gray-600">{employee.email}</td>
                                      {/* <td className="px-4 py-2 text-sm font-mono text-gray-600">{employee.employeeId || "-"}</td> */}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </TabsContent>

                      <TabsContent value="failed" className="flex-1 overflow-auto mt-4">
                        {importResults.failed.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            <div className="text-4xl mb-2">🎉</div>
                            <p>All employees were imported successfully!</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="bg-red-50 p-3 rounded-lg mb-3">
                              <p className="text-red-800 text-sm font-medium">
                                ❌ {importResults.failed.length} employee(s) failed to import
                              </p>
                              <p className="text-red-600 text-xs mt-1">Please check the errors and fix them in your Excel file</p>
                            </div>
                            <div className="border rounded-lg overflow-hidden">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">EmpID</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {importResults.failed.map((employee, idx) => (
                                    <tr key={idx} className="hover:bg-red-50">
                                      <td className="px-4 py-2 text-sm text-gray-900">{employee.name}</td>
                                      <td className="px-4 py-2 text-sm text-gray-600">{employee.email}</td>
                                      <td className="px-4 py-2 text-sm text-red-600">{employee.reason}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </TabsContent>
                    </Tabs>

                    <DialogFooter className="mt-4 pt-4 border-t">
                      <Button onClick={() => setImportResults(prev => ({ ...prev, show: false }))}>
                        Close
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>


                <Dialog open={showMandatoryModal} onOpenChange={setShowMandatoryModal}>
                  <DialogContent className="max-w-md bg-white">
                    <DialogHeader>
                      <DialogTitle>Mandatory Settings</DialogTitle>
                      <DialogDescription>
                        Control employee form validation
                      </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={settings.disable_mandatory}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              disable_mandatory: e.target.checked,
                            })
                          }
                        />

                        <label className="text-sm">
                          Disable Mandatory Fields
                        </label>
                      </div>

                      <Button
                        className="w-full bg-blue-600 text-white"
                        onClick={async () => {
                          await api.put("/update_employee_setting", settings);
                          setShowMandatoryModal(false);
                        }}
                      >
                        Save
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <Button
                  onClick={() => setShowMandatoryModal(true)}
                  className="gt-btn is-primary"
                >
                  ⚙ Employee Settings
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search employees..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by department" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {/* Sticky search bar — typing here filters the list below.
                      Event propagation is stopped so Radix Select doesn't
                      treat the keystrokes as typeahead navigation. */}
                  <div
                    className="sticky top-0 z-10 bg-white p-2 border-b"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="relative">
                      <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search department…"
                        value={deptFilterSearch}
                        onChange={(e) => setDeptFilterSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        className="w-full pl-7 pr-2 h-7 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                  </div>
                  {/* ~5 rows visible then scroll */}
                  <div className="max-h-[200px] overflow-y-auto">
                    {(() => {
                      const q = deptFilterSearch.trim().toLowerCase();
                      const filteredDepts = q
                        ? departmentList.filter((d: any) =>
                            (d.name || "").toLowerCase().includes(q),
                          )
                        : departmentList;
                      return (
                        <>
                          {(!q || "all departments".includes(q)) && (
                            <SelectItem value="all">All Departments</SelectItem>
                          )}
                          {filteredDepts.map((dept: any) => (
                            <SelectItem key={dept.id} value={dept.id}>
                              {dept.name}
                            </SelectItem>
                          ))}
                          {q && filteredDepts.length === 0 && !("all departments".includes(q)) && (
                            <div className="px-3 py-2 text-xs text-gray-500">No matches</div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </SelectContent>
              </Select>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <div
                    className="sticky top-0 z-10 bg-white p-2 border-b"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="relative">
                      <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search role…"
                        value={roleFilterSearch}
                        onChange={(e) => setRoleFilterSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        className="w-full pl-7 pr-2 h-7 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto">
                    {(() => {
                      const q = roleFilterSearch.trim().toLowerCase();
                      const filteredRoles = q
                        ? roles.filter((r) => (r || "").toLowerCase().includes(q))
                        : roles;
                      return (
                        <>
                          {(!q || "all roles".includes(q)) && (
                            <SelectItem value="all">All Roles</SelectItem>
                          )}
                          {filteredRoles.map((role) => (
                            <SelectItem key={role} value={role}>
                              {role.charAt(0).toUpperCase() + role.slice(1)}
                            </SelectItem>
                          ))}
                          {q && filteredRoles.length === 0 && !("all roles".includes(q)) && (
                            <div className="px-3 py-2 text-xs text-gray-500">No matches</div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
        
      {/* Employee Grid — paginated, 9 cards per page (3×3 grid on lg). */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {filteredEmployees
          .slice((currentPage - 1) * EMPLOYEES_PAGE_SIZE, currentPage * EMPLOYEES_PAGE_SIZE)
          .map((employee) => {
          // Show the name exactly as it was entered — no reformatting.
          // "CK" stays "CK", "McDonald" stays "McDonald", etc.
          const displayName =
            employee.name ||
            [employee.first_name, employee.last_name].filter(Boolean).join(" ");
          const pIdx = paletteIndex(displayName);
          return (
          <Card
            key={employee.id}
            className="gt-emp-card group"
          >
            {/* Colored top strip (palette-keyed by name) */}
            <div
              className="gt-emp-card__top-bar"
              data-palette={pIdx}
              aria-hidden="true"
            />

            <div className="gt-emp-card__body">

              {/* FIXED HEADER (flex based) */}
              <div className="flex items-start justify-between mb-4">

                {/* LEFT SIDE */}
                <div className="flex items-start gap-3 min-w-0">
                  <div className="gt-emp-card__avatar" data-palette={pIdx}>
                    {employee.profilePhoto ? (
                      <img src={employee.profilePhoto} alt={employee.name} />
                    ) : (
                      <span className="gt-emp-card__initial">
                        {(employee.name?.charAt(0) || employee.first_name?.charAt(0) || "U").toUpperCase()}
                      </span>
                    )}
                  </div>

                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 text-base truncate">
                      {displayName}
                    </h3>

                    <p className="text-sm text-gray-500 mt-0.5 truncate">
                      {resolveDesignation(employee)}
                    </p>

                    <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                      <span
                        className={`gt-emp-status-badge ${employee.is_active ? "is-active" : "is-inactive"}`}
                      >
                        <span className="gt-emp-status-badge__dot" aria-hidden="true" />
                        {employee.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* RIGHT SIDE (3 DOT MENU) */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gt-emp-card__menu-btn"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical className="h-4 w-4 text-gray-600" />
                    </Button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent align="end" className="w-48 bg-white">
                    <DropdownMenuItem
                      onClick={() => handleViewEmployee(employee.id, "personal")}
                      className="cursor-pointer"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Details
                    </DropdownMenuItem>

                    {canManageEmployees && (
                      <>
                        <DropdownMenuItem
                          onClick={() => handleEditClick(employee.id)}
                          className="cursor-pointer"
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Employee
                        </DropdownMenuItem>

                        <DropdownMenuItem
                          onClick={() => handleDeleteClick(employee)}
                          className="cursor-pointer text-red-600 focus:text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete/Deactivate
                        </DropdownMenuItem>

                        <DropdownMenuSeparator />

                        <DropdownMenuItem
                          onClick={() => handleOpenDocumentsTab(employee)}
                          className="cursor-pointer"
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          Send Documents
                        </DropdownMenuItem>

                        <DropdownMenuItem
                          onClick={async () => {
                            try {
                              const data = await getEmployeeById(employee.id);
                              setSelectedEmployee(data.employee);
                              setEditInitialTab("documents");
                              setShowEditModal(true);
                            } catch (error: any) {
                              toast({
                                description:
                                  error.message ||
                                  error.detail ||
                                  "Failed to load employee",
                                variant: "destructive",
                              });
                            }
                          }}
                          className="cursor-pointer"
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          Upload Documents
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Details Section — each item on its own row */}
              <div className="flex flex-col items-start gap-2 mt-4">
                {employee.email && (
                  <span className="gt-emp-card__chip is-email" title={employee.email}>
                    <Mail />
                    <span className="gt-emp-card__chip-text">{employee.email}</span>
                  </span>
                )}

                {employee.employeeID && (
                  <span className="gt-emp-card__chip is-id" title={employee.employeeID}>
                    <User />
                    <span className="gt-emp-card__chip-text">{employee.employeeID}</span>
                  </span>
                )}

                <div className="gt-emp-card__joined">
                  <Calendar />
                  <span>
                    Joined{" "}
                    {new Date(employee.hire_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              </div>

            </div>
          </Card>
          );
        })}
      </div>

      {/* Pagination — 9 cards per page */}
      {filteredEmployees.length > EMPLOYEES_PAGE_SIZE && (() => {
        const totalPages = Math.max(1, Math.ceil(filteredEmployees.length / EMPLOYEES_PAGE_SIZE));
        const safePage = Math.min(currentPage, totalPages);
        const start = (safePage - 1) * EMPLOYEES_PAGE_SIZE + 1;
        const end = Math.min(safePage * EMPLOYEES_PAGE_SIZE, filteredEmployees.length);
        const pageNumbers: (number | "…")[] = [];
        const push = (n: number | "…") => {
          if (pageNumbers[pageNumbers.length - 1] !== n) pageNumbers.push(n);
        };
        for (let p = 1; p <= totalPages; p++) {
          if (
            p === 1 ||
            p === totalPages ||
            (p >= safePage - 1 && p <= safePage + 1)
          ) {
            push(p);
          } else if (p === safePage - 2 || p === safePage + 2) {
            push("…");
          }
        }
        return (
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-gray-500">
              Showing <span className="font-medium text-gray-900">{start}</span>–
              <span className="font-medium text-gray-900">{end}</span> of{" "}
              <span className="font-medium text-gray-900">{filteredEmployees.length}</span> employees
            </p>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={safePage === 1}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Prev
              </button>
              {pageNumbers.map((p, i) =>
                p === "…" ? (
                  <span key={`e-${i}`} className="px-2 text-gray-400">…</span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setCurrentPage(p)}
                    className={`min-w-[34px] px-2.5 py-1.5 text-sm rounded-md border transition-colors ${
                      p === safePage
                        ? "bg-[var(--gt-primary,#0f2d1f)] text-white border-transparent"
                        : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    {p}
                  </button>
                ),
              )}
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        );
      })()}

        {/* View Employee Modal */}
        <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
          <DialogContent className="max-w-4xl bg-white max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Employee Details</DialogTitle>
            </DialogHeader>
            {selectedEmployee?.employee && (
              <EmployeeDetails
                employee={selectedEmployee.employee}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Employee Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          {/* Same two-region layout as the Add modal — the header + the
              EmployeeForm's sticky tab nav stay pinned while the body
              scrolls. The persistent "Save Changes" button lives in the
              non-scrolling header so it's always reachable. */}
          <DialogContent className="max-w-6xl bg-white max-h-[90vh] overflow-hidden p-0 gap-0 flex flex-col">
            {/* Header with title on the left and a persistent Save Changes
                button on the right — mirrors the TaskEdit pattern so the
                user can save from any tab without scrolling to the in-form
                button at the bottom. The button submits the form below
                via the `form="employee-form"` HTML attribute. */}
            <div className="shrink-0 bg-white">
              <DialogHeader className="px-6 pt-6 pb-3">
                <div className="flex items-start justify-between gap-4 pr-8">
                  <div>
                    <DialogTitle>Edit Employee</DialogTitle>
                    <DialogDescription>Update employee information.</DialogDescription>
                  </div>
                  <Button
                    type="submit"
                    form="employee-form"
                    size="sm"
                    className="shrink-0 bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 shadow-md"
                  >
                    <svg
                      className="w-3.5 h-3.5 mr-1.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Save Changes
                  </Button>
                </div>
              </DialogHeader>
              <EmployeeStepsTabNav
                activeTab={editActiveTab}
                onTabChange={makeTabChangeHandler(setEditActiveTab, editBodyRef)}
                className="px-2"
              />
            </div>
            <div ref={editBodyRef} className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
              {selectedEmployee?.employee && (
                <EmployeeForm
                  employee={selectedEmployee.employee}
                  onSubmit={handleEditEmployee}
                  currentUserRole={user?.role || "admin"}
                  initialTab={editInitialTab}
                  activeTab={editActiveTab}
                  onActiveTabChange={setEditActiveTab}
                  hideTabNav
                />
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Employee Modal */}
        <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Employee Action</DialogTitle>
              <DialogDescription>
                {selectedEmployeeForDelete?.is_active
                  ? "This will deactivate the employee."
                  : "Employee is inactive. Choose an action."}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2">
              {selectedEmployeeForDelete?.is_active && (
                <Button variant="destructive" onClick={handleSoftDelete}>
                  Deactivate
                </Button>
              )}
              {!selectedEmployeeForDelete?.is_active && (
                <>
                  <Button variant="outline" onClick={handleReactivate}>
                    Reactivate
                  </Button>
                  <Button variant="destructive" onClick={handlePermanentDelete}>
                    Permanent Delete
                  </Button>
                </>
              )}
              <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
